import { useEffect, useState } from "react";
import api from "../constant/api";
import { motion, AnimatePresence } from "framer-motion";
import { useMenuPermission } from "../hooks/useMenuPermission";

export default function Supplier() {
  // 1. Supplier menu ID-ai (4) hook-uku argument-ah pass seigirom
  const MENU_ID = 4; 
  const { permissions, loading } = useMenuPermission(MENU_ID);

  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");

  const [form, setForm] = useState({
    supplier_name: "",
    phone: "",
    email: "",
    address: "",
    gst_no: "",
  });

  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // =========================
  // FETCH SUPPLIERS
  // =========================
  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/supplier/getSuppliers");
      setSuppliers(res.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    // Component load aagumpodhu direct-ah data load aagum
    fetchSuppliers();
  }, []);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 font-medium">Checking permissions...</p>
      </div>
    );
  }

  // =========================
  // INPUT HANDLER
  // =========================
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // =========================
  // SUBMIT (SAVE / UPDATE)
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.post(`/supplier/updateSupplier/${editId}`, form);
        alert("✅ Supplier Updated");
      } else {
        await api.post("/supplier/createSupplier", form);
        alert("✅ Supplier Created");
      }
      fetchSuppliers();
      setShowModal(false);
      setEditId(null);
      setForm({
        supplier_name: "",
        phone: "",
        email: "",
        address: "",
        gst_no: "",
      });
    } catch (err) {
      console.log(err);
      alert("❌ Failed");
    }
  };

  // =========================
  // HANDLE EDIT
  // =========================
  const handleEdit = (item) => {
    setEditId(item.supplier_id);
    setForm({
      supplier_name: item.supplier_name || "",
      phone: item.phone || "",
      email: item.email || "",
      address: item.address || "",
      gst_no: item.gst_no || "",
    });
    setShowModal(true);
  };

  // =========================
  // HANDLE DELETE
  // =========================
  const handleDelete = async (id) => {
    const ok = window.confirm("Delete supplier?");
    if (!ok) return;
    try {
      await api.delete(`/supplier/deleteSupplier/${id}`);
      fetchSuppliers();
      alert("✅ Deleted");
    } catch (err) {
      console.log(err);
      alert("❌ Failed");
    }
  };

  // =========================
  // SEARCH FILTER
  // =========================
  const filteredSuppliers = suppliers.filter((s) =>
    s.supplier_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-5">
        <div>
          <h1 className="text-2xl font-bold">Supplier Management</h1>
          <p className="text-gray-500 text-sm">Manage all suppliers</p>
        </div>

        {/* 2. DYNAMIC ADD BUTTON SHOW/HIDE (canAdd) */}
        {permissions?.canAdd && (
          <button
            onClick={() => {
              setShowModal(true);
              setEditId(null);
              setForm({
                supplier_name: "",
                phone: "",
                email: "",
                address: "",
                gst_no: "",
              });
            }}
            className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-5 py-2 rounded-xl shadow-lg"
          >
            + Add Supplier
          </button>
        )}
      </div>

      {/* SEARCH BAR */}
      <div className="mb-5">
        <input
          type="text"
          placeholder="Search Supplier..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-80 border rounded-xl px-4 py-3 outline-none"
        />
      </div>

      {/* ======================================================== */}
      {/* DESKTOP TABLE VIEW */}
      {/* ======================================================== */}
      <div className="hidden md:block overflow-x-auto">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-900 to-gray-700 text-white">
              <tr>
                <th className="p-4 text-left">#</th>
                <th className="p-4 text-left">Supplier</th>
                <th className="p-4 text-left">Phone</th>
                <th className="p-4 text-left">Email</th>
                <th className="p-4 text-left">GST</th>
                {/* Action header automatically displays if either Edit or Delete permission is true */}
                {(permissions?.canEdit || permissions?.canDelete) && (
                  <th className="p-4 text-center">Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map((item, i) => (
                <tr key={item.supplier_id} className="border-b hover:bg-gray-50 transition">
                  <td className="p-4">{i + 1}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white flex items-center justify-center font-bold">
                        {item.supplier_name?.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold">{item.supplier_name}</h3>
                        <p className="text-sm text-gray-400">Supplier</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">{item.phone}</td>
                  <td className="p-4">{item.email}</td>
                  <td className="p-4">{item.gst_no}</td>
                  
                  {/* EDIT & DELETE ACTIONS CELL */}
                  {(permissions?.canEdit || permissions?.canDelete) && (
                    <td className="p-4">
                      <div className="flex justify-center gap-2">
                        {/* 3. DYNAMIC EDIT BUTTON (canEdit) */}
                        {permissions?.canEdit && (
                          <button
                            onClick={() => handleEdit(item)}
                            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
                          >
                            Edit
                          </button>
                        )}
                        {/* 4. DYNAMIC DELETE BUTTON (canDelete) */}
                        {permissions?.canDelete && (
                          <button
                            onClick={() => handleDelete(item.supplier_id)}
                            className="bg-red-500 text-white px-4 py-2 rounded-lg"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ======================================================== */}
      {/* MOBILE CARD UI VIEW */}
      {/* ======================================================== */}
      <div className="md:hidden flex flex-col gap-4">
        {filteredSuppliers.map((item) => (
          <div key={item.supplier_id} className="bg-white rounded-3xl shadow-lg border p-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white flex items-center justify-center text-xl font-bold">
                {item.supplier_name?.charAt(0)}
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-lg">{item.supplier_name}</h2>
                <p className="text-gray-400 text-sm">Supplier</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-400 text-xs">Phone</p>
                <p className="font-medium">{item.phone}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs">GST</p>
                <p className="font-medium">{item.gst_no}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-400 text-xs">Email</p>
                <p className="font-medium break-all">{item.email}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-400 text-xs">Address</p>
                <p className="font-medium">{item.address}</p>
              </div>
            </div>

            {/* MOBILE DYNAMIC ACTIONS */}
            {(permissions?.canEdit || permissions?.canDelete) && (
              <div className="flex gap-2 mt-5">
                {permissions?.canEdit && (
                  <button onClick={() => handleEdit(item)} className="flex-1 bg-blue-500 text-white py-2 rounded-xl">
                    Edit
                  </button>
                )}
                {permissions?.canDelete && (
                  <button onClick={() => handleDelete(item.supplier_id)} className="flex-1 bg-red-500 text-white py-2 rounded-xl">
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* MODAL COMPONENT */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 40 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white p-5 flex justify-between items-center">
                <h2 className="text-2xl font-bold">{editId ? "Edit Supplier" : "Add Supplier"}</h2>
                <button onClick={() => setShowModal(false)} className="text-2xl">✖</button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="supplier_name"
                  placeholder="Supplier Name"
                  value={form.supplier_name}
                  onChange={handleChange}
                  className="border rounded-xl px-4 py-3"
                  required
                />
                <input
                  type="text"
                  name="phone"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="border rounded-xl px-4 py-3"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={handleChange}
                  className="border rounded-xl px-4 py-3"
                />
                <input
                  type="text"
                  name="gst_no"
                  placeholder="GST No"
                  value={form.gst_no}
                  onChange={handleChange}
                  className="border rounded-xl px-4 py-3"
                />
                <textarea
                  rows="4"
                  name="address"
                  placeholder="Address"
                  value={form.address}
                  onChange={handleChange}
                  className="border rounded-xl px-4 py-3 md:col-span-2"
                />
                <button
                  type="submit"
                  className="md:col-span-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white py-3 rounded-xl font-semibold shadow-lg"
                >
                  {editId ? "Update Supplier" : "Save Supplier"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}